import React, { useEffect, useState } from 'react';
import { Divider } from 'primereact/divider';
import { Tag } from 'primereact/tag';
import { Carousel } from 'primereact/carousel';
import { Image } from 'primereact/image';
import { formatDate } from '../../utils/dateUtils';
import './GalleryTemplate.css';

const GalleryTemplate = ({ article }) => {
  const [showNav, setShowNav] = useState(false);

  useEffect(() => {
    setShowNav(window.innerWidth > 768);
  }, []);

  if (!article) return null;

  const ImageContainer = ({ image }) => (
    <div className="gallery-image-container">
      {/* Optional blurred background */}
      <img
        src={image.url}
        alt=""
        className="gallery-blurred-background"
      />
      {/* Foreground image */}
      <Image
        src={image.url}
        alt=""
        imageClassName="gallery-foreground-image"
        preview
      />
    </div>
  );

  const GalleryCarousel = ({ images, showNav, articleId }) => {
    const [isAutoplayActive, setIsAutoplayActive] = useState(true);

    const handleMouseEnter = () => {
      setIsAutoplayActive(false);
    };

    const handleMouseLeave = () => {
      setIsAutoplayActive(true);
    };

    const itemTemplate = (image, index) => (
      <div key={index} className="gallery-carousel-item">
        <ImageContainer image={image} />
      </div>
    );

    return (
      <div
        className="gallery-carousel-content"
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      >
        <div style={{ height: 'auto' }}>
          <Carousel
            value={images}
            itemTemplate={itemTemplate}
            numVisible={1}
            numScroll={1}
            autoplayInterval={isAutoplayActive ? 3000 : undefined}
            circular
            showNavigators={showNav}
            showIndicators={images.length > 1}
            className="gallery-carousel"
            key={`carousel-${images.length}-${articleId || 'default'}`}
            pt={{
              itemsContent: {
                className: 'flex items-center justify-center'
              },
              indicators: {
                className: 'gallery-carousel-indicators'
              },
              previousButton: {
                className: 'p-carousel-prev'
              },
              nextButton: {
                className: 'p-carousel-next'
              }
            }}
          />
        </div>
      </div>
    );
  };

  const renderImages = (images = []) => {
    if (images.length > 1) {
      return <GalleryCarousel images={images} showNav={showNav} articleId={article?.id} />;
    } else if (images.length === 1) {
      return (
        <div className="w-full">
          <ImageContainer image={images[0]} />
        </div>
      );
    }
    return null;
  };

  const TextWrapper = ({ children, className = '' }) => (
    <div
      className={`text-wrapper ${className}`}
      style={{
        wordWrap: 'break-word',
        overflowWrap: 'break-word',
        maxWidth: '100%',
      }}
    >
      {children}
    </div>
  );

  return (
    <article className="gallery-container">
      {/* Top Bar: Byline and Date */}
      <div className="gallery-top-bar">
        <span>{article.byline}</span>
        <span>{formatDate(article.updatedAt || article.createdAt)}</span>
      </div>

      {/* Divider */}
      <Divider className="gallery-divider" />

      {/* Banner Image */}
      {article.thumbnail?.url && (
        <div className="gallery-banner-container">
          <img
            src={article.thumbnail.url}
            alt={article.thumbnail.alt || article.title}
            className="gallery-banner-image"
          />
        </div>
      )}

      {/* Title */}
      <div className="flex justify-content-center">
        <TextWrapper>
          <h1 className="gallery-title">{article.title}</h1>
        </TextWrapper>
      </div>

      {/* Intro */}
      {article.introduction && (
        <div className="flex justify-content-center">
          <TextWrapper>
            <p className="gallery-intro">{article.introduction}</p>
          </TextWrapper>
        </div>
      )}

      {/* Divider */}
      <Divider className="gallery-divider mb-8" />

      {/* Sections */}
      {article.sections?.map((section, idx) => {
        const isEven = idx % 2 === 1;
        return (
          <section key={section.id || idx} className="gallery-section">
            <div className="gallery-grid">
              {isEven ? (
                <>
                  {/* Image Right */}
                  <div className="flex-1 h-full flex justify-center items-center max-w-[50%] overflow-hidden">
                    {renderImages(section.images)}
                  </div>
                  <div
                    className="flex-1 min-w-[30%] max-w-[50%] h-full flex items-center"
                    style={{ paddingTop: '1.5rem', lineHeight: '24px' }}
                  >
                    {section.description}
                  </div>
                </>
              ) : (
                <>
                  {/* Image Left */}
                  <div
                    className="flex-1 min-w-[30%] max-w-[50%] h-full flex items-center"
                    style={{ paddingTop: '1.5rem', lineHeight: '24px' }}
                  >
                    {section.description}
                  </div>
                  <div className="flex-1 h-full flex justify-center items-center max-w-[50%] overflow-hidden">
                    {renderImages(section.images)}
                  </div>
                </>
              )}
            </div>
          </section>
        );
      })}
    </article>
  );
};

export default GalleryTemplate;
